import React from "react";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./AdminSettings.css";

const formatFileSize = (size) => {
  const value = Number(size);
  if (!Number.isFinite(value) || value <= 0) return "0 KB";
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(2)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
};

const AdminSettings = () => {
  const [menuClose, setMenuClose] = React.useState(false);
  const [banners, setBanners] = React.useState([]);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const fileInputRef = React.useRef(null);

  const toggleMenu = () => setMenuClose((prev) => !prev);

  const loadBanners = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await Api.get("/public/login-banners");
      setBanners(Array.isArray(response?.data?.items) ? response.data.items : []);
    } catch (err) {
      console.error("Erro ao carregar banners:", err);
      setError("Nao foi possivel carregar os banners do carrossel.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const handleSelectFiles = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
    setError("");
    setSuccess("");
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (selectedFiles.length === 0) {
      setError("Selecione ao menos uma imagem para enviar.");
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("images", file));

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const response = await Api.post("/admin/login-banners", formData, {
        timeout: 30000,
      });
      setBanners(Array.isArray(response?.data?.items) ? response.data.items : []);
      setSelectedFiles([]);
      setSuccess("Banners enviados com sucesso. O carrossel ja foi atualizado.");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Erro ao enviar banners:", err);
      setError(
        err?.code === "ECONNABORTED"
          ? "O envio demorou mais do que o esperado. Tente novamente."
          : err?.response?.data?.message || "Nao foi possivel enviar os banners."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm("Deseja remover este banner do carrossel?")) return;

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      await Api.delete(`/admin/login-banners/${bannerId}`);
      setBanners((current) => current.filter((item) => item.id !== bannerId));
      setSuccess("Banner removido com sucesso.");
    } catch (err) {
      console.error("Erro ao remover banner:", err);
      setError(err?.response?.data?.message || "Nao foi possivel remover o banner.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} settingsActive="active" />

      <main className="main-content">
        <Topbar page="Configuracoes" onToggleMenu={toggleMenu} />

        <section className="admin-settings-grid">
          <article className="admin-settings-panel">
            <div className="admin-settings-header">
              <div>
                <h2>Carrossel da tela inicial</h2>
                <p>
                  Envie imagens para o banner do login. As fotos aparecem em sequencia no
                  carrossel automaticamente.
                </p>
              </div>
              <span className="admin-settings-badge">Super administrador</span>
            </div>

            <form className="admin-settings-form" onSubmit={handleUpload}>
              <label className="admin-upload-box" htmlFor="login-banners">
                <span>Selecionar imagens</span>
                <small>Formatos aceitos: JPG, PNG, WEBP. Limite de 10 MB por arquivo.</small>
              </label>

              <input
                ref={fileInputRef}
                id="login-banners"
                type="file"
                accept="image/*"
                multiple
                onChange={handleSelectFiles}
              />

              {selectedFiles.length > 0 ? (
                <div className="admin-selected-files">
                  {selectedFiles.map((file) => (
                    <div className="admin-selected-file" key={`${file.name}-${file.lastModified}`}>
                      <strong>{file.name}</strong>
                      <span>{formatFileSize(file.size)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="admin-settings-help">
                  A ordem do carrossel segue a ordem de envio. Para reorganizar, remova e envie
                  novamente na sequencia desejada.
                </p>
              )}

              <div className="admin-settings-actions">
                <button type="submit" disabled={submitting || selectedFiles.length === 0}>
                  {submitting ? "Enviando..." : "Enviar banners"}
                </button>
              </div>

              {error ? <div className="admin-settings-message error">{error}</div> : null}
              {success ? <div className="admin-settings-message success">{success}</div> : null}
            </form>
          </article>

          <article className="admin-settings-panel">
            <div className="admin-settings-header compact">
              <div>
                <h2>Banners cadastrados</h2>
                <p>{loading ? "Carregando imagens..." : `${banners.length} banner(s) ativo(s)`}</p>
              </div>
            </div>

            {loading ? (
              <div className="admin-settings-empty">Carregando banners...</div>
            ) : banners.length === 0 ? (
              <div className="admin-settings-empty">
                Nenhum banner enviado ainda. O login continua usando as imagens padrao.
              </div>
            ) : (
              <div className="admin-banner-grid">
                {banners.map((banner, index) => (
                  <article className="admin-banner-card" key={banner.id}>
                    <div className="admin-banner-preview">
                      <img src={banner.url} alt={banner.originalName || `Banner ${index + 1}`} />
                    </div>

                    <div className="admin-banner-body">
                      <strong>{banner.originalName || `Banner ${index + 1}`}</strong>
                      <span>Posicao {index + 1}</span>
                      <span>{formatFileSize(banner.size)}</span>
                    </div>

                    <button
                      type="button"
                      className="admin-banner-remove"
                      onClick={() => handleDelete(banner.id)}
                      disabled={submitting}
                    >
                      Remover
                    </button>
                  </article>
                ))}
              </div>
            )}
          </article>
        </section>
      </main>
    </div>
  );
};

export default AdminSettings;
